import React, { useRef, useState } from 'react'
import styled from 'styled-components'
import { Button } from '@nexiahome/ui-common/components/primitives/buttons'

import Modal from 'components/Modal'
import CollapsiblePanel from 'components/CollapsiblePanel'
import { removeScriptTags } from 'helpers/formatters'

import { color } from 'styles/colors'
import { screenSize } from 'styles/shared'

import { SiteMessageDetail } from 'types/SiteMessageDetail'

type props = {
  siteMessage: SiteMessageDetail
  className?: string,
  showCollapsed?: boolean | false,
  showDescription?: boolean | false,
  onImageClickCallback?: () => void
  onActionButtonClickCallback?: () => void
  onTextClickCallback?: () => void
}

const Body = ({
  siteMessage,
  onImageClickCallback,
  onActionButtonClickCallback,
  onTextClickCallback,
  className,
  showDescription = false,
  showCollapsed = false
}: props) => {
  const [showModal, setShowModal] = useState(false)
  const headerRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)

  const handleImageClick = (event: React.MouseEvent) => {
    event.preventDefault()
    if (showCollapsed && (!open || siteMessage.imageType === 'icon')) {
      headerRef.current?.click()
      return
    }

    if (siteMessage.imageType === 'image') {
      setShowModal(true)
      onImageClickCallback && onImageClickCallback()
    }
  }

  const TextWithActionButton = (
    <>
      <S.Message
        onClick={() => { if (onTextClickCallback) onTextClickCallback() }}
        dangerouslySetInnerHTML={{ __html: removeScriptTags(siteMessage.message) }}
      />
      {siteMessage.actionButtonCaption && (
        <S.ActionButton
          tabIndex={-1}
          href={siteMessage.actionButtonLink}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Button decoration="secondary" text={siteMessage.actionButtonCaption} onPress={() => { if (onActionButtonClickCallback) onActionButtonClickCallback() }} />
        </S.ActionButton>
      )}
    </>
  )

  const renderImage = () => {
    const { image, imageType } = siteMessage
    const isIcon = imageType === 'icon'
    const isImage = imageType === 'image'
    if (!image) return null

    return (
      <S.ImageContainer onClick={handleImageClick} isTypeImage={isImage}>
        <S.Image
          src={image}
          alt={image ? 'Site image' : 'No image available'}
          open={open}
          role="img"
          icon={isIcon}
        />
      </S.ImageContainer>
    )
  }

  return (
    <>
      <S.Container className={className} imageType={siteMessage.imageType} open={open}>
        {showCollapsed
          ? (
            <S.CollapsiblePanel caretHorizontalPosition="left" caretVerticalPosition="start" onClickCallback={() => setOpen(!open)} headerComponent={
              <S.ImageAndTextContainer>
                {renderImage()}
                <S.TitleAndMessageContainer>
                  <S.Title ref={headerRef} dangerouslySetInnerHTML={{ __html: removeScriptTags(siteMessage.title) }} />
                  {!open && showDescription && <S.PreviewMessage dangerouslySetInnerHTML={{ __html: removeScriptTags(siteMessage.message) }} />}
                  { open && <S.MessageText>{TextWithActionButton}</S.MessageText>}
                </S.TitleAndMessageContainer>
              </S.ImageAndTextContainer>
            }
            />
          )
          : (
            <S.ImageAndTextContainer>
              {renderImage()}
              <S.TitleAndMessageContainer>
                <S.Title dangerouslySetInnerHTML={{ __html: removeScriptTags(siteMessage.title) }} />
                <S.MessageText>{TextWithActionButton}</S.MessageText>
              </S.TitleAndMessageContainer>
            </S.ImageAndTextContainer>
          )
        }

      </S.Container>
      <S.Modal
        variant="lg"
        messageType={siteMessage.messageType}
        title={siteMessage.title}
        isDisplayed={showModal}
        closeCallback={() => setShowModal(false)}
      >
        {siteMessage.image && <S.ModalImage src={siteMessage.image} alt="" /> /* alt intentionally blank as the src is dynamic */}

        <S.ModalMessageText>
          {TextWithActionButton}
        </S.ModalMessageText>
      </S.Modal>
    </>
  )
}

export default Body

const largeImageContainerWidth = '120px'
const mediumImageContainerWidth = '100px'
const iconContainerWidth = '64px'

const S = {
  Container: styled.div.withConfig({
    shouldForwardProp: (prop) => !['imageType', 'open'].includes(prop)
  })<{ imageType: string, open: boolean }>`
    display: flex;
    flex-direction: row;
    overflow: hidden;
    
    @media ${screenSize.small} {
      display: ${({ imageType, open }) => imageType === 'image' && open ? 'block' : 'flex'};
    }
  `,
  ImageContainer: styled.div.withConfig({
    shouldForwardProp: (prop) => !['isTypeImage'].includes(prop)
  })<{ isTypeImage: boolean}>`
    width: ${({ isTypeImage }) => isTypeImage ? largeImageContainerWidth : iconContainerWidth};
    min-width:  ${({ isTypeImage }) => isTypeImage ? largeImageContainerWidth : iconContainerWidth};
    margin-right: 10px;
    transition: width 0.5s;
    cursor: pointer;
    @media ${screenSize.medium} {
      width: ${({ isTypeImage }) => isTypeImage ? mediumImageContainerWidth : iconContainerWidth};
    }

    @media ${screenSize.small} {
      width: ${({ isTypeImage }) => isTypeImage ? '100px' : iconContainerWidth};
    }
  `,
  Image: styled.img.withConfig({
    shouldForwardProp: (prop) => !['open', 'icon'].includes(prop)
  })<{ open: boolean, icon: boolean }>`
    display: block;
    margin: 0 auto;
    width: 100%;
    ${({ open, icon }) => !open && icon && 'height: 30px; width: 30px;'}
  `,
  ImageAndTextContainer: styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    @media ${screenSize.small} {
      flex-direction: column;
      gap: 10px;
      align-items: flex-start;
    }
  `,
  CollapsiblePanel: styled(CollapsiblePanel)`
    width: 100%;
    & svg{
      margin-top: auto;
      margin-bottom: auto;
    }
  `,
  MessageText: styled.div`
    display: inline-block;
    width: 100%;
    word-break: break-word;
    padding-right: 10px;
  `,
  Title: styled.div`
    width: 100%;
    font-weight: bold;
    font-size: 1em;
    overflow: hidden;
    text-overflow: ellipsis;
  `,
  Message: styled.div``,
  PreviewMessage: styled.div`
    width: 100%;
    overflow: hidden;
    white-space: nowrap;
    display: block;
    text-overflow: ellipsis;
  `,
  TitleAndMessageContainer: styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
    width: 80%;
  `,
  ActionButton: styled.a`
    margin: 10px 0 auto auto;
    width: fit-content;
    display: block;
  `,
  ModalImage: styled.img`
    display: block;
    margin: 0 auto;
    border: 1px solid ${color('border')};
    max-width: 100%;
  `,
  ModalMessageText: styled.div`
    display: inline-block;
    margin-top: 20px;
    font-size: 1.2em;
  `,
  Modal: styled(Modal).withConfig({
    shouldForwardProp: (prop) => !['messageType'].includes(prop)
  })<{ messageType: string }>`
    background-color: ${({ messageType }) => messageType === 'info' ? color('siteMessageInfoBackground') : color('siteMessageWarningBackground')};
    @media ${screenSize.small} {
      min-height: 100%;
    }
  `
}
